import { Box3, Vector3 } from "three";

export type ColliderKind = "wall" | "lintel" | "floor" | "ceiling";

export type CollisionBody = {
  name: string;
  kind: ColliderKind;
  bounds: Box3;
};

export type VisitorCapsule = {
  radius: number;
  height: number;
  eyeHeight: number;
};

export const VISITOR_CAPSULE: VisitorCapsule = {
  radius: 0.3,
  height: 1.75,
  eyeHeight: 1.65,
};

export function moveWithCollisions(
  position: Vector3,
  displacement: Vector3,
  colliders: readonly CollisionBody[],
  capsule: VisitorCapsule = VISITOR_CAPSULE,
  maxStep = 0.1,
): Vector3 {
  const result = position.clone();
  const horizontalDistance = Math.hypot(displacement.x, displacement.z);
  const steps = Math.max(1, Math.ceil(horizontalDistance / maxStep));
  const stepX = displacement.x / steps;
  const stepZ = displacement.z / steps;
  for (let index = 0; index < steps; index += 1) {
    const proposedX = result.clone();
    proposedX.x += stepX;
    if (!isBlocked(proposedX, colliders, capsule)) result.x = proposedX.x;
    const proposedZ = result.clone();
    proposedZ.z += stepZ;
    if (!isBlocked(proposedZ, colliders, capsule)) result.z = proposedZ.z;
  }
  return result;
}

export function isBlocked(
  eyePosition: Vector3,
  colliders: readonly CollisionBody[],
  capsule: VisitorCapsule = VISITOR_CAPSULE,
): boolean {
  const bottom = eyePosition.y - capsule.eyeHeight;
  const top = bottom + capsule.height;
  return colliders.some((collider) => {
    if (collider.kind === "floor" || collider.kind === "ceiling") return false;
    if (top <= collider.bounds.min.y || bottom >= collider.bounds.max.y) return false;
    return (
      eyePosition.x > collider.bounds.min.x - capsule.radius &&
      eyePosition.x < collider.bounds.max.x + capsule.radius &&
      eyePosition.z > collider.bounds.min.z - capsule.radius &&
      eyePosition.z < collider.bounds.max.z + capsule.radius
    );
  });
}

export function validateCollisionBody(body: CollisionBody): void {
  const values = [
    body.bounds.min.x,
    body.bounds.min.y,
    body.bounds.min.z,
    body.bounds.max.x,
    body.bounds.max.y,
    body.bounds.max.z,
  ];
  if (
    values.some((value) => !Number.isFinite(value)) ||
    body.bounds.isEmpty() ||
    body.bounds.min.x >= body.bounds.max.x ||
    body.bounds.min.y >= body.bounds.max.y ||
    body.bounds.min.z >= body.bounds.max.z
  ) {
    throw new Error(`GLB_COLLIDER_INVALID: ${body.name}`);
  }
}
